import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import ImageUploader from '@/admin/ImageUploader';
import VideoUploader from '@/admin/VideoUploader';
import { TextField, NumberField, Toggle, Section, Row, SaveBar } from '@/admin/AdminUI';

const SECTIONS = [
  { key: 'experiences', label: 'Experiences (Section 02)' },
  { key: 'speakers', label: 'Speakers (Section 03)' },
  { key: 'partners', label: 'Partners + Gallery (Section 05)' },
];

const REVEAL_KEYS = [
  { k: 'hero_entrance', label: 'Hero entrance sequence' },
  { k: 'experiences_stagger', label: 'Experiences card stagger' },
  { k: 'speakers_cascade', label: 'Speakers cascade reveal' },
  { k: 'featured_session', label: 'Featured Session reveal' },
  { k: 'registration', label: 'Registration reveal' },
  { k: 'partners', label: 'Partners reveal' },
  { k: 'gallery', label: 'Gallery reveal' },
];

const WOW_KEYS = [
  { k: 'magnetic_cursor', label: 'Magnetic cursor on primary buttons' },
  { k: 'number_ticker', label: 'Number ticker on hero stats' },
  { k: 'card_tilt_experiences', label: '3D card tilt (Experiences)' },
  { k: 'marquee_partners', label: 'Marquee logos (Partners)' },
  { k: 'gradient_mesh_experiences', label: 'Gradient mesh (Experiences)' },
  { k: 'gradient_mesh_footer', label: 'Gradient mesh (Footer)' },
  { k: 'cursor_spotlight_registration', label: 'Cursor spotlight (Registration)' },
];

export default function MotionVisualTab() {
  const [motion, setMotion] = useState(null);
  const [sections, setSections] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then((r) => {
      setMotion(r.data?.motion || {});
      setSections(r.data?.sections || {});
    });
  }, []);

  if (!motion || !sections) return <div className="font-body text-ink-muted">Loading…</div>;

  const setMotionField = (path, value) => {
    const next = { ...motion };
    const parts = path.split('.');
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = { ...(cur[parts[i]] || {}) };
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
    setMotion(next);
  };
  const setSection = (key, field, value) => {
    setSections({ ...sections, [key]: { ...(sections[key] || {}), [field]: value } });
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put('/admin/settings/motion', motion);
      await api.put('/admin/settings/section-backgrounds', sections);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Section title="Global Animation Controls">
        <Toggle label="Master animation ON" value={motion.master_enabled !== false} onChange={(v) => setMotionField('master_enabled', v)} />
        <Row>
          <div>
            <label className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-2">Animation Speed</label>
            <div className="flex gap-2">
              {['slow', 'normal', 'fast'].map((s) => (
                <button key={s} type="button" onClick={() => setMotionField('speed', s)}
                  className={'font-body font-semibold text-[11px] uppercase tracking-wider2 px-3 py-1.5 rounded-full border ' + ((motion.speed || 'normal') === s ? 'bg-brand-orange text-white border-brand-orange' : 'border-border-line text-ink-primary')}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-2">Mobile Motion Behavior</label>
            <div className="flex gap-2">
              {['full', 'reduced', 'off'].map((s) => (
                <button key={s} type="button" onClick={() => setMotionField('mobile_behavior', s)}
                  className={'font-body font-semibold text-[11px] uppercase tracking-wider2 px-3 py-1.5 rounded-full border ' + ((motion.mobile_behavior || 'reduced') === s ? 'bg-brand-orange text-white border-brand-orange' : 'border-border-line text-ink-primary')}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </Row>
        <div className="text-[11px] font-body text-ink-soft mt-2"><b>Accessibility:</b> prefers-reduced-motion is always respected (locked on, OS-level setting).</div>
      </Section>

      <Section title="Per-Section Animation Toggles">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {REVEAL_KEYS.map((r) => (
            <Toggle key={r.k} label={r.label} value={(motion.reveals || {})[r.k] !== false} onChange={(v) => setMotionField(`reveals.${r.k}`, v)} />
          ))}
        </div>
      </Section>

      <Section title="Wow Factors">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {WOW_KEYS.map((r) => (
            <Toggle key={r.k} label={r.label} value={(motion.wow || {})[r.k] !== false} onChange={(v) => setMotionField(`wow.${r.k}`, v)} />
          ))}
        </div>
        <Row>
          <div>
            <label className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-2">Partners Display Mode</label>
            <div className="flex gap-2">
              {['marquee', 'grid'].map((s) => (
                <button key={s} type="button" onClick={() => setMotionField('wow.marquee_mode', s)}
                  className={'font-body font-semibold text-[11px] uppercase tracking-wider2 px-3 py-1.5 rounded-full border ' + (((motion.wow || {}).marquee_mode || 'marquee') === s ? 'bg-brand-orange text-white border-brand-orange' : 'border-border-line text-ink-primary')}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-2">Marquee Speed</label>
            <div className="flex gap-2">
              {['slow', 'normal', 'fast'].map((s) => (
                <button key={s} type="button" onClick={() => setMotionField('wow.marquee_speed', s)}
                  className={'font-body font-semibold text-[11px] uppercase tracking-wider2 px-3 py-1.5 rounded-full border ' + (((motion.wow || {}).marquee_speed || 'normal') === s ? 'bg-brand-orange text-white border-brand-orange' : 'border-border-line text-ink-primary')}>{s}</button>
              ))}
            </div>
          </div>
        </Row>
      </Section>

      <Section title="Section Backgrounds">
        {SECTIONS.map(({ key, label }) => {
          const s = sections[key] || {};
          return (
            <div key={key} className="p-4 border border-border-line rounded-sm bg-white space-y-4 mb-4">
              <div className="font-display font-bold uppercase text-[14px] text-ink-primary">{label}</div>
              <Row>
                <div>
                  <label className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-2">Background Type</label>
                  <div className="flex gap-2">
                    {['color', 'image', 'video'].map((t) => (
                      <button key={t} type="button" onClick={() => setSection(key, 'background_type', t)}
                        className={'font-body font-semibold text-[11px] uppercase tracking-wider2 px-3 py-1.5 rounded-full border ' + ((s.background_type || 'color') === t ? 'bg-brand-orange text-white border-brand-orange' : 'border-border-line text-ink-primary')}>{t}</button>
                    ))}
                  </div>
                </div>
                <NumberField label="Overlay Opacity (0–0.8)" value={s.overlay_opacity ?? 0} min={0} max={0.8} step={0.05} onCommit={(n) => setSection(key, 'overlay_opacity', n)} />
              </Row>
              {(s.background_type || 'color') === 'color' && (
                <TextField label="Background Color (hex)" value={s.background_value || ''} onChange={(v) => setSection(key, 'background_value', v)} />
              )}
              {(s.background_type === 'image' || s.background_type === 'video') && (
                <ImageUploader value={s.background_image} onChange={(v) => setSection(key, 'background_image', v)} label="Background Image (also poster if video)" />
              )}
              {s.background_type === 'video' && (
                <>
                  <VideoUploader value={s.background_video} onChange={(v) => setSection(key, 'background_video', v)} label="Background Video (MP4)" />
                  <Toggle label="Use video if uploaded (else image)" value={!!s.use_video} onChange={(v) => setSection(key, 'use_video', v)} />
                </>
              )}
            </div>
          );
        })}
      </Section>

      <SaveBar onSave={save} saving={saving} saved={saved} />
    </div>
  );
}
